import React, { useEffect, useState, useCallback, useRef } from "react";
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	addToast,
	ToastProvider,
} from "@heroui/react";

import axios from "axios";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import DynamicCard from "./DynamicCard";
import BVPlayer from "./BVPlayer";
import { motion } from "motion/react";
import { useInView } from "react-intersection-observer";

function LazyBVPlayer(props) {
	const { ref, inView } = useInView({
		triggerOnce: true,
		rootMargin: "200px 0px",
	});

	return (
		<div ref={ref} style={{ minHeight: "240px", width: "100%" }}>
			{inView && <BVPlayer bv={props.bv} />}
		</div>
	);
}

function DelectIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			className="icon"
			viewBox="0 0 1024 1024"
			width={24}
			height={24}
		>
			<path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z" />
		</svg>
	);
}

function isNumber(str) {
	return !isNaN(Number(str)) && str.trim() !== "";
}

function CommentForm(props) {
	const sanitizeSchema = {
		...defaultSchema,
		tagNames: [
			...(defaultSchema.tagNames || []),
			"audio",
			"video",
			"source",
			"bv-player",
		],
		attributes: {
			...defaultSchema.attributes,
			audio: ["src", "controls", "loop", "muted", "autoplay"],
			video: [
				"src",
				"controls",
				"width",
				"height",
				"loop",
				"muted",
				"autoplay",
				"poster",
			],
			source: ["src", "type"],
			"bv-player": ["bv"],
		},
	};

	const PAGE_SIZE = 50;
	const [text, setText] = useState("");
	const [page, setPage] = useState(1);
	const [list, setList] = useState([]);
	const [totalPages, setTotalPages] = useState(1);
	const [isLoading, setIsLoading] = useState(false);

	const loadingRef = useRef(false);
	const pageRef = useRef(1);
	const totalPagesRef = useRef(1);

	const fetchComments = async (targetPage, isReset = false) => {
		if (loadingRef.current) return;
		loadingRef.current = true;
		setIsLoading(true);

		try {
			const session = localStorage.getItem("session") || "";
			const res = await axios.get(
				`/api/comments/list?session=${session}&ps=${targetPage}&pn=${PAGE_SIZE}`,
			);
			const data = res.data.items ?? [];
			const total = res.data.total ?? 1;

			pageRef.current = targetPage;
			totalPagesRef.current = total;

			setPage(targetPage);
			setTotalPages(total);
			setList((prev) => (isReset ? data : [...prev, ...data]));
		} catch (err) {
			console.error("加载留言失败:", err);
		} finally {
			loadingRef.current = false;
			setIsLoading(false);
		}
	};

	// 模态框打开时拉取第一页并重置
	useEffect(() => {
		if (props.isOpen) {
			fetchComments(1, true);
		}
	}, [props.isOpen]);

	// 底部哨兵节点触底检测
	const { ref: loadMoreTriggerRef, inView } = useInView({
		threshold: 0,
		rootMargin: "100px",
	});

	useEffect(() => {
		if (
			inView &&
			!loadingRef.current &&
			pageRef.current < totalPagesRef.current
		) {
			fetchComments(pageRef.current + 1, false);
		}
	}, [inView]);

	return (
		<div className="max-h-[60vh]">
			<ToastProvider placement="top-right" />
			<Modal
				isOpen={props.isOpen}
				onOpenChange={props.onChange}
				scrollBehavior="inside"
				className="overflow-x-hidden"
			>
				<ModalContent>
					<ModalHeader className="flex flex-col gap-1">
						留言板
					</ModalHeader>
					<ModalBody>
						{/* 原生滚动容器 */}
						<div className="overflow-y-auto max-h-[50vh] overflow-x-hidden pr-1">
							{list.map((item, i) => (
								<motion.div
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.3 }}
									key={item.ID || `comment-${i}`}
									className={`flex flex-row items-center markdown-body !mt-1.5 ${
										i === 0
											? ""
											: "border-t-1.5 border-dashed pt-2"
									}`}
								>
									{item.Text !== "" && (
										<div className="flex-1">
											{window.config.COMMENT_OWNER.indexOf(
												item.DisplayName,
											) !== -1 ? (
												<span
													className={"text-[#865ab8]"}
												>
													OWNER {" :"}
												</span>
											) : (
												<span>
													{item.DisplayName}
													{" :"}
												</span>
											)}
											<Markdown
												remarkPlugins={[remarkGfm]}
												rehypePlugins={[
													rehypeRaw,
													[
														rehypeSanitize,
														sanitizeSchema,
													],
												]}
												components={{
													a: ({ node, ...props }) => (
														<a
															className="text-blue-500 hover:text-blue-700 hover:underline"
															target="_blank"
															rel="noreferrer"
															{...props}
														/>
													),
													p: "span",
													img: ({
														node,
														...props
													}) => (
														<img
															{...props}
															className="inline-block"
														/>
													),
													audio: ({
														node,
														...props
													}) => (
														<audio
															{...props}
															className="inline-block h-8"
														/>
													),
													ul: ({
														node,
														...props
													}) => (
														<ul
															style={{
																listStyleType:
																	"disc",
																paddingLeft:
																	"2em",
															}}
															{...props}
														/>
													),
													"bili-dynamic-card": ({
														node,
														...props
													}) => (
														<DynamicCard
															OID={props.OID}
														/>
													),
													"bv-player": ({
														node,
														...props
													}) => (
														<LazyBVPlayer
															bv={props.bv}
														/>
													),
												}}
											>
												{item.Text}
											</Markdown>
										</div>
									)}
									{item.Session && (
										<Button
											isIconOnly
											startContent={<DelectIcon />}
											onClick={() => {
												axios
													.post(
														"/api/comments/delete",
														new URLSearchParams({
															id: item.ID,
															session:
																localStorage.getItem(
																	"session",
																) || "",
														}),
													)
													.then(() => {
														fetchComments(1, true);
													});
											}}
											className="ml-4"
										/>
									)}
								</motion.div>
							))}

							{/* 触底观察哨兵 */}
							<div
								ref={loadMoreTriggerRef}
								className="py-2 text-center text-xs text-gray-400"
							>
								{isLoading && "加载中..."}
								{!isLoading &&
									page >= totalPages &&
									list.length > 0 &&
									"没有更多留言了"}
							</div>
						</div>

						<div className="flex flex-row items-center mt-2">
							<Input
								label=""
								onValueChange={(text) => setText(text)}
								value={text}
								placeholder="发表评论..."
							/>
							<Button
								className="ml-2"
								onClick={() => {
									if (
										isNumber(text) ||
										isNumber(text.replace("UID:", ""))
									) {
										addToast({
											title: "Alert",
											description:
												"DONT SPAM（不要发送无意义内容）",
											color: "danger",
										});
									} else {
										axios
											.post(
												"/api/comments/send",
												new URLSearchParams({
													text: text,
													session:
														localStorage.getItem(
															"session",
														) || "",
												}),
											)
											.then(() => {
												fetchComments(1, true);
											});
									}
									setText("");
								}}
							>
								发送
							</Button>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button color="primary" onPress={props.onClose}>
							关闭
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</div>
	);
}

export default CommentForm;
